// ModalAddPhoto.js
// @author: anghello

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Col, Modal, ModalBody, ModalHeader, Row, Input, Button, InputGroup, InputGroupAddon } from 'reactstrap';
import Strings from '../../translations/Translations';
import SortableList, { SortableItem } from "react-easy-sort";
import { arrayMoveImmutable } from 'array-move';
import FileHelper from '../../helpers/FileHelper';
import { useAuth } from '../../Context/Auth/AuthContext';
import PSO from '../../services/PSO';
import { toast } from 'react-toastify';
import axios from 'axios';
import Unsplash from '../../services/Unsplash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import FilesUploadAllowed from '../../enums/FilesUploadAllowed';
import RemixHelper from '../../helpers/RemixHelper';
import ProcessImageForUpload from '../../helpers/ProcessImageForUpload';

const mediaPackCategoriesJsonSrc = process.env.PUBLIC_URL + '/mediapackcategories.json';
const ENABLE_UNSPLASH_PICKER = (process.env.REACT_APP_ENABLE_UNSPLASH_PICKER === 'true');

export default function ModalAddPhoto(props) {
  const { modalOpen, toggleModal, rid, callback } = props;
  const filesInput = useRef();
  const [remixMedia, setRemixMedia] = useState([]);

  const { authenticated, toggleLoading } = useAuth();

  const [genresMap, setGenresMap] = useState();
  const [currentMediaPackStyles, setCurrentMediaPackStyles] = useState();

  const [modalMediaPack, setModalMediaPack] = useState(false);
  const toggleModalMediapack = useCallback(() => {
    setModalMediaPack(!modalMediaPack);
  }, [setModalMediaPack, modalMediaPack]);

  const [modalUnsplashPicker, setModalUnsplashPicker] = useState(false);
  const toggleModalUnsplashPicker = useCallback(() => {
    setModalUnsplashPicker(!modalUnsplashPicker);
  }, [setModalUnsplashPicker, modalUnsplashPicker]);

  const [unsplashRandom, setUnsplashRandom] = useState(false);
  const [unsplashPhotos, setUnsplashPhotos] = useState();
  const [unsplashCurrentPhoto, setUnsplashCurrentPhoto] = useState();
  const [unsplashQuerySearch, setUnsplashQuerySearch] = useState();

  const onSortEnd = useCallback((oldIndex, newIndex) => {
    setRemixMedia((array) => arrayMoveImmutable(array, oldIndex, newIndex));
  }, [setRemixMedia]);

  const removeMedia = useCallback((index) => {
    if (index !== undefined && remixMedia) {
      let media = remixMedia;
      if (media.length > 0) {
        media = media.filter((value, i) => {
          return index !== i;
        });
      }
      setRemixMedia(media);
    }
  }, [remixMedia, setRemixMedia]);

  const setFiles = useCallback(async (evt) => {
    const files = evt.target.files;

    if (files && files.length > 0) {
      let filteredFiles = [];

      for (let i = 0; i < files.length; i ++) {
        const { type, name } = files[i];
        if (FilesUploadAllowed.includes(type)) {
          // Filetype is allowed
          filteredFiles.push(files[i]);
        } else {
          // Filetype is not allowed
          toast.error(`${Strings('filetype_not_allowed')}: ${name}`);
        }
      }

      let media = remixMedia;
      if (!remixMedia) {
        media = [];
      }
      const newMedia = await RemixHelper.processItsForMediaGrid(filteredFiles, 'file');
      media = media.concat(newMedia);
      setRemixMedia(media);
    }
  }, [remixMedia, setRemixMedia]);

  const triggerFilesPrompt = useCallback(() => {
    filesInput.current.click();
  }, []);

  const uploadMedia = useCallback(async () => {
    // Sending the media
    toggleModal();

    if (remixMedia.length > 0) {
      let result = true;

      for (let i = 0; i < remixMedia.length; i++) {
        toggleLoading(true, `${Strings('sending_media')} ${i+1} of ${remixMedia.length}`);

        const currentImage = remixMedia[i];

        if (currentImage && currentImage.type === 'mediapack') {
          try {
            const sendPhotoArgs = {
              al: authenticated.al,
              un: authenticated.un,
              rid: rid,
              uptype: 1,
              upfn: currentImage.eid
            }
            await PSO.sendPhotoToRemix(sendPhotoArgs);
          } catch (error) {
            console.error(Strings('error_on_sending_media'));
            result = false;
          }
        } else if (currentImage && currentImage.type === 'file') {
          const targetFile = await ProcessImageForUpload(
            currentImage.file,
            process.env.REACT_APP_PZ_EXIF_JS,
            process.env.REACT_APP_MIN_UPLOAD_PHOTO_WIDTH,
            process.env.REACT_APP_MIN_UPLOAD_PHOTO_HEIGHT,
            process.env.REACT_APP_MAX_UPLOAD_PHOTO_WIDTH,
            process.env.REACT_APP_MAX_UPLOAD_PHOTO_HEIGHT,
            true
          );
          if (targetFile && targetFile !== undefined) {
            // const fileDataURI = currentImage.src;
            // const fileBLOB = FileHelper.dataURItoBlob(fileDataURI);
            try {
              const sendPhotoArgs = {
                al: authenticated.al,
                un: authenticated.un,
                rid: rid,
                Filedata: targetFile,
                Filename: targetFile.name
              }
              await PSO.sendPhotoToRemix(sendPhotoArgs);
            } catch (error) {
              console.error(Strings('error_on_sending_media'));
              result = true;
            }
          }
        } else if (currentImage && currentImage.type === 'unsplash') {
          const targetFile = currentImage;
          if (targetFile && targetFile !== undefined) {
            const fileDataURI = currentImage.src;
            const fileBLOB = FileHelper.dataURItoBlob(fileDataURI);
            try {
              const sendPhotoArgs = {
                al: authenticated.al,
                un: authenticated.un,
                rid: rid,
                Filedata: fileBLOB,
                Filename: targetFile.name
              }
              await PSO.sendPhotoToRemix(sendPhotoArgs);
            } catch (error) {
              console.error(Strings('error_on_sending_media'));
            }
          }
        } else if (currentImage && currentImage.type === 'video') {
          const targetFile = currentImage;
          if (targetFile && targetFile !== undefined) {
            const fileDataURI = currentImage.src;
            const fileBLOB = FileHelper.dataURItoBlob(fileDataURI);
            try {
              const sendVideoArgs = {
                al: authenticated.al,
                un: authenticated.un,
                rid,
                Filedata: fileBLOB,
                Filename: targetFile.file.name
              };
              await PSO.sendVideoToRemix(sendVideoArgs);
            } catch (error) {
              console.error(error);
            }
          }
        }
      }

      if (result) {
        toast.success(Strings('all_photos_sent_sucessfully'));
        setRemixMedia([]);
      } else {
        toast.error(Strings('an_error_ocorred_sending_the_photos'));
        toggleModal();
      }

      if (callback) {
        callback();
      }
    }
    toggleLoading(false);
  }, [authenticated, remixMedia, rid, toggleLoading, setRemixMedia, toggleModal, callback]);

  const getMediaPackCategories = useCallback(async () => {
    toggleLoading(true);
    try {
      const _t = new Date().getTime();
      const mediaPackCategoriesJson = await axios.get(`${mediaPackCategoriesJsonSrc}?_t=${_t}`);

      if (mediaPackCategoriesJson && mediaPackCategoriesJson.data && mediaPackCategoriesJson.data.its) {
        setGenresMap(mediaPackCategoriesJson.data.its);
      }
    } catch (err) {
      console.error(err);
    }
    toggleLoading(false);
  }, [toggleLoading]);

  const getMediaPack = useCallback(async (catid) => {
    if (catid) {
      toggleLoading(true);

      const mediaPackArgs = {
        un: authenticated.un,
        al: authenticated.al,
        catid: catid,
        effect: 'e1'
      }

      try {
        const response = await PSO.getMediaPack(mediaPackArgs);

        if (response.result === 'success' && response.its) {
          let media = remixMedia;
          if (!remixMedia) {
            media = [];
          }

          const newMedia = await RemixHelper.processItsForMediaGrid(response.its, 'mediapack');
          media = media.concat(newMedia);
          setRemixMedia(media);
        } else {
          const message = (response && response.error) ? response.error : Strings('error_on_request');
          toast.error(message);
        }
      } catch (err) {
        toast.error(Strings('error_on_get_media_pack'));
      }

      toggleLoading(false);
    }
  }, [toggleLoading, authenticated, setRemixMedia, remixMedia]);

  const getUnsplashRandomPhotos = useCallback(async () => {
    if (!unsplashRandom) {
      toggleLoading(true, Strings('getting_unsplash_photos'));
      try {
        const photos = await Unsplash.getRandomPhotos();
        if (photos && photos.length > 0) {
          const columns = [[], [], [], [], []];
          let column = 0;
          for (let i = 0; i < photos.length; i++) {
            columns[column].push(photos[i]);
            column = (column === columns.length - 1) ? 0 : column + 1;
          }

          setUnsplashRandom(true);
          setUnsplashPhotos(columns);
        }
      } catch (err) {
        console.error(err);
        toast.error(Strings('error_getting_unsplash_photos'));
      }
      toggleLoading(false);
    }
  }, [toggleLoading, unsplashRandom, setUnsplashRandom]);

  const getUnsplashQueryPhotos = useCallback(async () => {
    setUnsplashCurrentPhoto();

    if (unsplashQuerySearch) {
      toggleLoading(true, Strings('getting_unsplash_photos'));
      try {
        const response = await Unsplash.queryPhotos(unsplashQuerySearch);
        if (response && response.results && response.results.length > 0) {
          const photos = response.results;
          const columns = [[], [], [], [], []];
          let column = 0;
          for (let i = 0; i < photos.length; i++) {
            columns[column].push(photos[i]);
            column = (column === columns.length - 1) ? 0 : column + 1;
          }
          setUnsplashRandom(false);
          setUnsplashPhotos(columns);
        }
      } catch (err) {
        console.error(err);
        toast.error(Strings('error_getting_unsplash_photos'));
      }
      toggleLoading(false);
    } else {
      getUnsplashRandomPhotos();
    }
  }, [setUnsplashCurrentPhoto, unsplashQuerySearch, toggleLoading, getUnsplashRandomPhotos]);

  const selectUnsplashPhoto = useCallback(async (photo) => {
    toggleLoading(true);
    let media;
    if (remixMedia) {
      media = remixMedia;
    } else {
      media = [];
    }
    const newMedia = await RemixHelper.processItsForMediaGrid([photo], 'unsplash');
    if (newMedia) {
      media = media.concat(newMedia);
      setRemixMedia(media);
      setUnsplashCurrentPhoto();
      toggleModalUnsplashPicker();
    }
    toggleLoading(false);
  }, [remixMedia, setRemixMedia, toggleLoading, setUnsplashCurrentPhoto, toggleModalUnsplashPicker]);
  
  useEffect(() => {
    getMediaPackCategories();
  }, [getMediaPackCategories]);

  return (
    <>
      <Modal isOpen={modalOpen} toggle={toggleModal} size='lg'>
        <ModalHeader toggle={toggleModal}>
          {Strings('add_photo')}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <Button
                color='primary'
                className='mr-2'
                onClick={toggleModalMediapack}
              >
                {Strings('use_media_pack')}
              </Button>
              {ENABLE_UNSPLASH_PICKER && (
                <Button
                  color='primary'
                  className='mr-2'
                  onClick={() => {
                    toggleModalUnsplashPicker();
                    getUnsplashRandomPhotos();
                  }}
                >
                  {Strings('use_unsplash')}
                </Button>
              )}
              <Button
                color='primary'
                className='mr-2'
                onClick={triggerFilesPrompt}
              >
                {Strings('upload_photo_clip')}
              </Button>
            </Col>
          </Row>

          <Row className='mt-3'>
            <Col md={12}>
              <SortableList
                onSortEnd={onSortEnd}
                className='media-grid-root'
                draggedItemClassName='media-grid-dragged'
              >
                {remixMedia && remixMedia.length > 0 && remixMedia.map(({ src, type }, index) => (
                  <SortableItem key={src}>
                    <div className='media-grid-item'>
                      {type !== 'video' && (
                        <img
                          src={src}
                          style={{ height: 150 }}
                          alt={src}
                        />
                      )}
                      {type === 'video' && (
                        <video
                          playsInline
                          controls
                          controlsList='nodownload noplaybackrate noremoteplayback'
                          disablePictureInPicture
                          style={{ height: 150 }}
                        >
                          <source src={src} />
                        </video>
                      )}
                      <button
                        className='media-grid-delete-button btn-danger'
                        onClick={() => removeMedia(index)}
                      >
                        <i className="pe-7s-trash"> </i>
                      </button>
                    </div>
                  </SortableItem>
                ))}
              </SortableList>
            </Col>
          </Row>

          {remixMedia && remixMedia.length > 0 && (
            <Row className='mt-3'>
              <Col>
                <Button color='success' onClick={uploadMedia}>
                  {Strings('upload_selected_photos_clips')}
                </Button>
              </Col>
            </Row>
          )}

          <Row className='d-none'>
            <Col>
              <Input
                type='file'
                name='remix_files'
                id='remix_files'
                multiple
                onChange={setFiles}
                innerRef={filesInput}
                accept='image/*, video/*'
              />
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={modalMediaPack}
        toggle={toggleModalMediapack}
      >
        <ModalHeader toggle={toggleModalMediapack}>
          {Strings('media_pack_modal_title')}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <Input
                type='select'
                onChange={(evt) => {
                  const style = evt.target.value;
                  if (style && genresMap[style] && genresMap[style].its) {
                    setCurrentMediaPackStyles(genresMap[style].its);
                  } else {
                    setCurrentMediaPackStyles();
                  }
                }}
              >
                <option value=''>{Strings('select_a_style')}</option>
                {genresMap && genresMap.map((value, index) => (
                  <option key={value.name} value={index}>{value.name}</option>
                ))}
              </Input>
            </Col>
          </Row>

          <Row className='mt-3 text-center'>
            <Col>
              {currentMediaPackStyles && currentMediaPackStyles.map((value, index) => (
                <Button
                  className='mr-2 mb-2'
                  outline
                  color='primary'
                  key={`${value.catid}_${index}`}
                  onClick={() => {
                    toggleModalMediapack();
                    getMediaPack(value.catid);
                    setCurrentMediaPackStyles();
                  }}
                >
                  {value.name}
                </Button>
              ))}
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={modalUnsplashPicker}
        toggle={toggleModalUnsplashPicker}
        size="lg"
      >
        <ModalHeader toggle={toggleModalUnsplashPicker}>
          {Strings('unsplash_picker')}
        </ModalHeader>
        <ModalBody>

          <Row className='mb-4'>
            <Col>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <div className="input-group-text">
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                </InputGroupAddon>
                <Input
                  placeholder={`${Strings('search_free_photos_on_unsplash')}...`}
                  onChange={(evt) => {
                    setUnsplashQuerySearch(evt.target.value);
                  }}
                />
                <InputGroupAddon addonType="append">
                  <Button
                    onClick={getUnsplashQueryPhotos}
                  >
                    {Strings('search')}
                  </Button>
                </InputGroupAddon>
              </InputGroup>
            </Col>
          </Row>

          <Row>
            <Col md={(unsplashCurrentPhoto) ? 9 : 12}>
              <div className='photo-grid'>
                {unsplashPhotos && unsplashPhotos.map((column, index) => (
                  <div className='photo-grid-column' key={index}>
                    {column && column.length > 0 && column.map((photo) => (
                      <div
                        key={photo.id}
                        className="d-inline-block mr-2 mb-2 cursor-pointer"
                        onClick={() => {
                          setUnsplashCurrentPhoto(photo);
                        }}
                      >
                        <img
                          src={photo.urls.regular}
                          className='img-fluid'
                          alt={photo.id}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Col>

            {unsplashCurrentPhoto && (
              <Col md={3}>
                <div>
                  <img
                    src={unsplashCurrentPhoto.urls.regular}
                    className='img-fluid'
                    alt={unsplashCurrentPhoto.id}
                  />
                  <div
                    className='media-grid-close-icon'
                    onClick={() => {
                      setUnsplashCurrentPhoto();
                    }}
                  >
                    <i className="pe-7s-close"> </i>
                  </div>
                </div>
                <div className='text-right'>
                  <Button
                    className='mt-2'
                    color='primary'
                    size='sm'
                    outline
                    onClick={() => {
                      selectUnsplashPhoto(unsplashCurrentPhoto);
                    }}
                  >
                    {Strings('use_photo')}
                  </Button>
                </div>
              </Col>
            )}
          </Row>
          
        </ModalBody>
      </Modal>
    </>
  );
}
